import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Bug, CreditCard, KeyRound, LayoutDashboard, Loader2, LogOut, Mail, Shield, UserRound } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { IssueReportDialog } from "@/components/shared/IssueReportDialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { getApiBaseUrl } from "@/lib/config";
import { getPasswordPolicyErrorMessage, getPasswordRuleItems, passwordMeetsAllRules } from "@/lib/passwordPolicy";

const API = getApiBaseUrl();

export const UserAccountMenu = ({ user, isAdmin, onLogout, onBlockedNavigation, triggerClassName = "", contentAlign = "end", testIdPrefix = "user-account-menu", usernameClassName = "", showAdminBadge = true }) => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const { language } = useLanguage();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [usageInfo, setUsageInfo] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const passwordRuleItems = getPasswordRuleItems(language, form.new_password);
  const passwordRulesSatisfied = passwordMeetsAllRules(form.new_password);
  const confirmPasswordStarted = form.confirm_password.length > 0;
  const passwordsMatch = form.new_password === form.confirm_password;

  const text = useMemo(() => language === "es" ? {
    account: "Mi cuenta",
    accountHint: "Administra tu perfil, plan y seguridad desde aquí.",
    dashboard: "Dashboard",
    billing: "Facturación",
    admin: "Admin",
    logout: "Cerrar sesión",
    email: "Correo",
    membership: "Membresía",
    joined: "Registro",
    usage: "Uso del plan hoy",
    minutesUsed: "Minutos usados",
    minutesRemaining: "Minutos restantes",
    dailyLimit: "Límite diario",
    security: "Seguridad",
    currentPassword: "Clave actual",
    newPassword: "Nueva clave",
    confirmPassword: "Confirmar nueva clave",
    savePassword: "Actualizar clave",
    forgotPassword: "¿Olvidaste tu clave?",
    reportIssue: "Reportar problema",
    openAccount: "Abrir cuenta",
    passwordUpdated: "Clave actualizada correctamente",
  } : {
    account: "My account",
    accountHint: "Manage your profile, plan, and security from here.",
    dashboard: "Dashboard",
    billing: "Billing",
    admin: "Admin",
    logout: "Logout",
    email: "Email",
    membership: "Membership",
    joined: "Joined",
    usage: "Today's plan usage",
    minutesUsed: "Minutes used",
    minutesRemaining: "Minutes remaining",
    dailyLimit: "Daily limit",
    security: "Security",
    currentPassword: "Current password",
    newPassword: "New password",
    confirmPassword: "Confirm new password",
    savePassword: "Update password",
    forgotPassword: "Forgot your password?",
    reportIssue: "Report issue",
    openAccount: "Open account",
    passwordUpdated: "Password updated successfully",
  }, [language]);

  useEffect(() => {
    if (!dialogOpen) return;
    const loadUsage = async () => {
      try {
        setUsageLoading(true);
        const response = await axios.get(`${API}/usage`);
        setUsageInfo(response.data);
      } catch (error) {
        console.error("Failed to load usage", error);
      } finally {
        setUsageLoading(false);
      }
    };
    loadUsage();
  }, [dialogOpen]);

  const navigateSafely = (path) => {
    if (onBlockedNavigation && !onBlockedNavigation(path)) return;
    navigate(path);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm_password) {
      toast.error(language === "es" ? "Las contraseñas no coinciden" : "Passwords do not match");
      return;
    }
    if (!passwordMeetsAllRules(form.new_password)) {
      toast.error(getPasswordPolicyErrorMessage(language));
      return;
    }
    try {
      setPasswordLoading(true);
      await axios.post(`${API}/auth/change-password`, form);
      toast.success(text.passwordUpdated);
      setForm({ current_password: "", new_password: "", confirm_password: "" });
      await refreshUser();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to update password");
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className={`gap-2 max-w-[260px] text-sm sm:text-base font-semibold ${triggerClassName}`.trim()} data-testid={`${testIdPrefix}-trigger`}>
            <UserRound className="w-5 h-5" />
            <span className={`truncate ${usernameClassName}`.trim()}>{user.username}</span>
            {isAdmin && showAdminBadge && <Badge data-testid={`${testIdPrefix}-admin-badge`}>Admin</Badge>}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align={contentAlign} className="w-72" data-testid={`${testIdPrefix}-content`}>
          <DropdownMenuLabel>
            <div className="space-y-1">
              <p className="text-sm font-semibold" data-testid={`${testIdPrefix}-name`}>{user.username}</p>
              <p className="text-sm text-muted-foreground" data-testid={`${testIdPrefix}-email`}>{user.email}</p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem className="text-sm py-2.5" onClick={() => setDialogOpen(true)} data-testid={`${testIdPrefix}-open-profile-button`}>
            <UserRound className="w-5 h-5" />
            {text.openAccount}
          </DropdownMenuItem>
          <DropdownMenuItem className="text-sm py-2.5" onClick={() => navigateSafely('/dashboard')} data-testid={`${testIdPrefix}-dashboard-button`}>
            <LayoutDashboard className="w-5 h-5" />
            {text.dashboard}
          </DropdownMenuItem>
          <DropdownMenuItem className="text-sm py-2.5" onClick={() => navigateSafely('/billing')} data-testid={`${testIdPrefix}-billing-button`}>
            <CreditCard className="w-5 h-5" />
            {text.billing}
          </DropdownMenuItem>
          <DropdownMenuItem className="text-sm py-2.5" onClick={() => setReportDialogOpen(true)} data-testid={`${testIdPrefix}-report-issue-button`}>
            <Bug className="w-5 h-5" />
            {text.reportIssue}
          </DropdownMenuItem>
          {isAdmin && (
            <DropdownMenuItem className="text-sm py-2.5" onClick={() => navigateSafely('/admin')} data-testid={`${testIdPrefix}-admin-button`}>
              <Shield className="w-5 h-5" />
              {text.admin}
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem className="text-sm py-2.5" onClick={onLogout} data-testid={`${testIdPrefix}-logout-button`}>
            <LogOut className="w-5 h-5" />
            {text.logout}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto overscroll-contain" data-testid={`${testIdPrefix}-dialog`}>
          <DialogHeader>
            <DialogTitle>{text.account}</DialogTitle>
            <DialogDescription>{text.accountHint}</DialogDescription>
          </DialogHeader>

          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-4 rounded-2xl border border-border p-4" data-testid={`${testIdPrefix}-details-card`}>
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{text.email}</p>
                <p className="mt-1 text-sm font-medium" data-testid={`${testIdPrefix}-email-value`}>{user.email}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{text.membership}</p>
                <p className="mt-1 text-sm font-medium capitalize" data-testid={`${testIdPrefix}-membership-value`}>{user.membership}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{text.joined}</p>
                <p className="mt-1 text-sm font-medium" data-testid={`${testIdPrefix}-joined-value`}>
                  {new Date(user.created_at).toLocaleDateString(language === "es" ? "es-CO" : "en-US")}
                </p>
              </div>
            </div>

            <div className="space-y-4 rounded-2xl border border-border p-4" data-testid={`${testIdPrefix}-usage-card`}>
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{text.usage}</p>
                {usageLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <div>
                  <p className="text-xs text-muted-foreground">{text.minutesUsed}</p>
                  <p className="text-lg font-semibold" data-testid={`${testIdPrefix}-usage-used-value`}>{usageInfo ? Number(usageInfo.minutes_used || 0).toFixed(2) : "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{text.minutesRemaining}</p>
                  <p className="text-lg font-semibold" data-testid={`${testIdPrefix}-usage-remaining-value`}>{usageInfo ? Number(usageInfo.minutes_remaining || 0).toFixed(2) : "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{text.dailyLimit}</p>
                  <p className="text-lg font-semibold" data-testid={`${testIdPrefix}-usage-limit-value`}>{usageInfo ? Number(usageInfo.daily_limit || 0).toFixed(2) : "—"}</p>
                </div>
              </div>
            </div>
          </div>

          <form className="space-y-4 rounded-2xl border border-border p-4" onSubmit={handlePasswordChange} data-testid={`${testIdPrefix}-password-form`}>
            <div className="flex items-center gap-2">
              <KeyRound className="h-4 w-4 text-primary" />
              <p className="font-medium">{text.security}</p>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="current-password">{text.currentPassword}</Label>
                <Input id="current-password" type="password" value={form.current_password} onChange={(e) => setForm((prev) => ({ ...prev, current_password: e.target.value }))} data-testid={`${testIdPrefix}-current-password-input`} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-password">{text.newPassword}</Label>
                <Input id="new-password" type="password" value={form.new_password} onChange={(e) => setForm((prev) => ({ ...prev, new_password: e.target.value }))} data-testid={`${testIdPrefix}-new-password-input`} />
                <div className="space-y-1" data-testid={`${testIdPrefix}-password-rule-list`}>
                  {passwordRuleItems.map((rule) => (
                    <p
                      key={rule.key}
                      className={`text-xs font-medium transition-colors ${rule.satisfied ? "text-emerald-600" : "text-amber-600"}`}
                      data-testid={`${testIdPrefix}-password-${rule.key}-message`}
                    >
                      {rule.satisfied ? "✓ " : "• "}
                      {rule.label}
                    </p>
                  ))}
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted" data-testid={`${testIdPrefix}-password-strength-bar-track`}>
                  <div
                    className={`h-full rounded-full transition-all ${passwordRulesSatisfied ? "bg-emerald-500" : "bg-amber-500"}`}
                    style={{ width: `${(passwordRuleItems.filter((rule) => rule.satisfied).length / passwordRuleItems.length) * 100}%` }}
                    data-testid={`${testIdPrefix}-password-strength-bar-fill`}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">{text.confirmPassword}</Label>
                <Input id="confirm-password" type="password" value={form.confirm_password} onChange={(e) => setForm((prev) => ({ ...prev, confirm_password: e.target.value }))} data-testid={`${testIdPrefix}-confirm-password-input`} />
                <p
                  className={`text-xs font-medium transition-colors ${!confirmPasswordStarted ? "text-muted-foreground" : passwordsMatch ? "text-emerald-600" : "text-destructive"}`}
                  data-testid={`${testIdPrefix}-password-match-message`}
                >
                  {!confirmPasswordStarted
                    ? (language === "es" ? "Confirma la nueva clave para continuar" : "Confirm the new password to continue")
                    : passwordsMatch
                      ? (language === "es" ? "✓ Las contraseñas coinciden" : "✓ Passwords match")
                      : (language === "es" ? "Las contraseñas no coinciden" : "Passwords do not match")}
                </p>
              </div>
            </div>
            <DialogFooter className="items-center justify-between gap-3 sm:flex-row sm:justify-between sm:space-x-0">
              <Button type="button" variant="link" className="px-0" onClick={() => navigate(`/reset-password?email=${encodeURIComponent(user.email)}`)} data-testid={`${testIdPrefix}-forgot-password-link`}>
                <Mail className="mr-2 h-4 w-4" />
                {text.forgotPassword}
              </Button>
              <Button type="submit" disabled={passwordLoading} data-testid={`${testIdPrefix}-save-password-button`}>
                {passwordLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : text.savePassword}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <IssueReportDialog open={reportDialogOpen} onOpenChange={setReportDialogOpen} />
    </>
  );
};